// Prompt ideas: photo -> suggestions -> pick -> prefilled Generate. Uses the
// `prompt-ideas` alias (prompt_suggestions_dev, 01-definitions.md #8),
// submitting one seed per suggestion (ADR-0008's "same inputs -> same
// prompt" seeding applies here too) and polling the Task to DONE.
//
// prompt_suggestions_dev's final output (`prompt`) is scalar-kind, so a
// DONE slot carries it in `value`, not `artifact_id` (08-contracts.md
// #1.1, 02-core.md #10 -- see docs/plan/notes/PROPOSED-scalar-result-values.md
// for the resolution history: this was originally an undocumented gap,
// closed by main commit a4b31be "Give scalar final outputs a value field
// in result slots").
//
// The photo stays on the left while the ideas fill the right, so picking
// one never loses sight of what it is about.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { allocateArtifact, createTask, getTask, type TaskView } from "../api/client";
import { uploadToStore } from "../api/store";
import { generateVariantSeeds, newTaskId } from "../lib/generate";
import { useCatalog } from "../lib/catalog";

const SUGGESTION_COUNT = 4;
const POLL_MS = 1000;
const POLL_TIMEOUT_MS = 60000;

interface Suggestion {
  seedIndex: number;
  text?: string;
  state: string;
}

export default function PromptIdeas() {
  const navigate = useNavigate();
  const catalog = useCatalog();
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [artifactId, setArtifactId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  function onFile(file: File) {
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
    setSuggestions([]);
    setArtifactId(null);
  }

  async function onGetIdeas() {
    if (!photo) return;
    setBusy(true);
    setError(null);
    setSuggestions([]);
    try {
      const contentType = photo.type || "application/octet-stream";
      const alloc = await allocateArtifact(contentType, photo.size);
      await uploadToStore(alloc.upload_url, alloc.upload_token, photo, contentType);
      setArtifactId(alloc.artifact_id);

      const seeds = generateVariantSeeds(SUGGESTION_COUNT);
      const task = await createTask({
        task_id: newTaskId(),
        alias: "prompt-ideas",
        inputs: { image: [alloc.artifact_id], seed: seeds },
      });
      setSuggestions(seeds.map((_, i) => ({ seedIndex: i, state: "PENDING" })));

      const finalTask = await pollUntilTerminal(task.task_id);
      setSuggestions(
        finalTask.results.map((r, i) => ({
          seedIndex: i,
          state: r.state,
          text: r.state === "DONE" && typeof r.value === "string" ? r.value : undefined,
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to generate ideas");
    } finally {
      setBusy(false);
    }
  }

  function useSuggestion(text: string) {
    navigate("/", {
      state: {
        alias: "photo-to-video-1080p",
        hint: text,
        artifactId: artifactId ?? undefined,
        artifactLabel: photo?.name,
      },
    });
  }

  // ADR-0047: the Catalog's prompt_ideas flag; the nav link is hidden too.
  if (catalog?.features.prompt_ideas === false) {
    return (
      <div className="u-page">
        <div className="u-head">
          <h1 className="u-h1">Prompt ideas</h1>
          <p className="u-lede">Not available yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="u-page">
      <div className="u-head">
        <div>
          <h1 className="u-h1">Prompt ideas</h1>
          <p className="u-lede">
            Give the model a photo and it writes a few ways to describe moving it. Pick one and it
            comes back to the bench as your hint.
          </p>
        </div>
      </div>

      <div className="u-ideas">
        <aside className="u-console">
          {preview ? (
            <img className="u-ref" src={preview} alt="" />
          ) : (
            <div className="u-drop">
              <span className="u-drop__hint">No photo yet</span>
              <span className="u-drop__sub">one still is enough</span>
            </div>
          )}
          <label className="u-btn">
            {photo ? "Choose another photo" : "Choose a photo"}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
            />
          </label>
          <button className="u-btn u-btn--solid u-btn--wide" disabled={!photo || busy} onClick={() => void onGetIdeas()}>
            {busy ? "Thinking…" : "Get ideas"}
          </button>
          {busy && (
            <div className="u-bar">
              <span className="u-bar__stripes" />
            </div>
          )}
          {error && <p className="u-error">{error}</p>}
        </aside>

        <section>
          {suggestions.length === 0 ? (
            <p className="u-empty">
              Ideas appear here — {SUGGESTION_COUNT} of them, one per seed.
            </p>
          ) : (
            <ul className="u-grid u-grid--wide">
              {suggestions.map((s) => (
                <li key={s.seedIndex} className={s.text ? "u-idea" : "u-idea u-idea--busy"}>
                  <p className="u-idea__text">
                    {s.text ?? (s.state === "PENDING" || s.state === "RUNNING" ? "thinking…" : s.state.toLowerCase())}
                  </p>
                  {s.text && (
                    <button className="u-btn u-btn--wide" onClick={() => useSuggestion(s.text as string)}>
                      Use this
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

async function pollUntilTerminal(taskId: string): Promise<TaskView> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  for (;;) {
    const task = await getTask(taskId);
    if (task.state === "DONE" || task.state === "FAILED" || task.state === "CANCELLED") return task;
    if (Date.now() > deadline) return task;
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
  }
}
